// 261010 Epic 2 / P3d — bộ điều phối poll THUẦN của phiếu AI chấm (mẫu `styleProfilePoller` / `locationViewPoller`). Không React / antd ⇒ test bằng jest fake timers; hook React chỉ bọc mỏng bộ này.
// Luật: có phiếu đang chạy còn trong hạn (BE báo `running.overdue = false`; quá 15 phút BE tự báo true) ⇒ gọi lại `listStepReviews` mỗi 8 giây · request trước chưa về ⇒ bỏ nhịp · hết RUNNING ⇒ dừng và báo "xong" đúng MỘT lần
// · lỗi mạng ⇒ giữ dữ liệu cũ, báo lỗi, DỪNG (Tải lại mới bật lại) · phiếu quá hạn ⇒ không poll mãi (job đã chết).
import type { StepReviewsResponse } from '../../types/filmStepReview';
import { shouldPollReviews } from './stepReviewPolicy';

export const POLL_MS = 8000;

export const shouldPoll = shouldPollReviews;

export interface StepReviewPollerDeps {
  fetchReviews: () => Promise<StepReviewsResponse>;
  /** Dữ liệu mới của một lượt poll / Tải lại (không gọi khi lỗi). */
  onData: (r: StepReviewsResponse) => void;
  /** Phiếu RUNNING vừa kết thúc (lần trước còn RUNNING, lần này không): kèm dữ liệu của CHÍNH lượt đó. */
  onFinished?: (r: StepReviewsResponse) => void;
  /** Lượt poll / Tải lại lỗi mạng: poll đã dừng, dữ liệu cũ giữ nguyên. */
  onError?: (e: unknown) => void;
  intervalMs?: number;
}

export interface StepReviewPoller {
  /** Host báo "đây là dữ liệu mới nhất" (nạp đầu, sau thao tác). Bật / tắt timer theo `shouldPoll`; không tự bật lại khi đang lỗi. */
  sync: (r: StepReviewsResponse) => void;
  /** Nút "Tải lại": gỡ cờ lỗi, nạp ngay một lượt, bật lại poll nếu còn RUNNING. */
  reload: () => Promise<void>;
  stop: () => void;
  isActive: () => boolean;
  isErrored: () => boolean;
}

export function createStepReviewPoller(deps: StepReviewPollerDeps): StepReviewPoller {
  const intervalMs = deps.intervalMs ?? POLL_MS;
  let current: StepReviewsResponse | null = null;
  let wasRunning = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let inflight = false;
  let errored = false;
  let stopped = false;
  let gen = 0; // tăng mỗi lần `sync`: kết quả poll xuất phát TRƯỚC một `sync` mới hơn là dữ liệu cũ ⇒ bỏ

  const clear = () => { if (timer !== null) { clearInterval(timer); timer = null; } };
  const arm = () => {
    if (stopped) return;
    if (!errored && shouldPoll(current)) { if (timer === null) timer = setInterval(() => { void run(false); }, intervalMs); } else clear();
  };
  /** Ghi nhận dữ liệu mới; true = phiếu RUNNING vừa kết thúc. */
  const observe = (r: StepReviewsResponse): boolean => {
    current = r;
    const running = shouldPoll(r);
    const done = wasRunning && !running;
    wasRunning = running;
    return done;
  };

  async function run(manual: boolean): Promise<void> {
    if (stopped || inflight) return; // không chồng: bỏ nhịp
    inflight = true;
    const myGen = gen;
    try {
      const r = await deps.fetchReviews();
      if (stopped) return;
      if (myGen !== gen) return; // đã có dữ liệu mới hơn (sync) ⇒ bỏ kết quả cũ; timer do `sync` lo
      const done = observe(r);
      deps.onData(r);
      if (done) deps.onFinished?.(r);
    } catch (e) {
      if (stopped) return;
      errored = true; clear();
      deps.onError?.(e);
      return;
    } finally {
      inflight = false;
    }
    if (manual) errored = false;
    arm();
  }

  return {
    sync(r) {
      if (stopped) return;
      gen += 1;
      const done = observe(r);
      arm();
      if (done) deps.onFinished?.(r);
    },
    async reload() {
      if (stopped) return;
      errored = false;
      await run(true);
    },
    stop() { stopped = true; clear(); },
    isActive: () => timer !== null,
    isErrored: () => errored,
  };
}
